import { CONSTRUCTOR_PODS, SEED_ITEM_PODS } from 'constants/constructorConstants';
import { QUERY_PARAMS } from 'constants/Shared';
import basketConstants from 'constants/Basket';
import urlUtils from 'utils/Url';
const { addParam } = urlUtils;
const { DELIVERY_OPTIONS } = basketConstants;
import localeUtils from 'utils/LanguageLocale';

/**
 * Transforms a recommendations array (mocked or real) into the format expected by the RecommendationsCarousel.
 * @param {Array} recommendations - Array of recommendation objects
 * @returns {Array} Transformed recommendations for carousel display
 */
function transformRecommendationsForCarousel(recommendations = [], podId = null) {
    const isAutoReplenishPod =
        podId === CONSTRUCTOR_PODS.AUTO_REPLENISH_CHOSEN_FOR_YOU || podId === CONSTRUCTOR_PODS.AUTO_REPLENISH_SEPHORA_COLLECTION;

    return recommendations.map(recommendation => {
        const data = recommendation.data || recommendation.product || {};
        const currentSku = data.currentSku || {};
        const variationsMap = recommendation.variationsMap || {};

        function formatPriceRangeToString(minPrice, maxPrice) {
            if (!minPrice || !maxPrice) {
                return null;
            }

            const formattedMin = minPrice.toFixed(2);
            const formattedMax = maxPrice.toFixed(2);

            return formattedMin === formattedMax ? `$${formattedMax}` : `$${formattedMin} - $${formattedMax}`;
        }

        let listPrice = data.listPrice;
        let salePrice = data.salePrice;

        if (variationsMap) {
            const { minListPrice, maxListPrice, minSalePrice, maxSalePrice } = variationsMap;

            const formattedList = formatPriceRangeToString(minListPrice, maxListPrice);

            if (formattedList) {
                listPrice = formattedList;
            }

            const formattedSale = formatPriceRangeToString(minSalePrice, maxSalePrice);

            if (formattedSale) {
                salePrice = formattedSale;
            }
        }

        const convertPrice = price => {
            if (price) {
                const convertedPrice = `${price.replaceAll('.', ',').replaceAll('$', '').replaceAll('-', '$ -')} $`;

                return localeUtils.isFrench() ? convertedPrice : price;
            }

            return null;
        };

        const convertValuePrice = price => {
            if (price == null || price === '') {
                return null;
            }

            const numericValue = typeof price === 'number' ? price : parseFloat(String(price).replace(/[^0-9.]/g, ''));

            if (isNaN(numericValue)) {
                return null;
            }

            return localeUtils.getFormattedPrice(numericValue, false);
        };

        // Get base URL
        let targetUrl = data.url;

        // Add AUTO_REPLENISH query parameter if needed
        if (isAutoReplenishPod && targetUrl) {
            targetUrl = addParam(targetUrl, QUERY_PARAMS.DELIVERY_OPTION, DELIVERY_OPTIONS.AUTO_REPLENISH.toLowerCase());
        }

        return {
            badgeAltText: currentSku.imageAltText,
            biExclusiveLevel: currentSku.biExclusivityLevel,
            brandName: data.brandName,
            starRatings: data.rating,
            size: currentSku.constructorSizeRefinement || '',
            productReviewCount: data.totalReviews,
            image: data.imageUrl,
            isAppExclusive: !!currentSku.isAppExclusive,
            isBiOnly: !!currentSku.isBI,
            isFirstAccess: !!currentSku.isFirstAccess,
            isFree: !!currentSku.isFree,
            isLimitedEdition: !!currentSku.isLimitedEdition,
            isLimitedQuantity: !!currentSku.isLimitedQuantity,
            isLimitedTimeOffer: !!currentSku.isLimitedTimeOffer,
            isNew: !!currentSku.isNew,
            isOnlineOnly: !!currentSku.isOnlineOnly,
            isOutOfStock: !!currentSku.isOutOfStock,
            isSephoraExclusive: !!currentSku.isSephoraExclusive,
            listPrice: convertPrice(listPrice),
            salePrice: convertPrice(salePrice),
            valuePrice: convertValuePrice(currentSku?.constructorValuePrice),
            wholeSalePrice: convertPrice(data.wholesalePrice),
            productId: data.productId,
            productName: recommendation.value,
            skuId: currentSku.skuId,
            skuImages: {
                imageUrl: data.imageUrl
            },
            smallImage: currentSku.skuImages?.image135,
            targetUrl: targetUrl,
            variationId: data.variationId,
            variationType: currentSku.variationValue,
            variationTypeDisplayName: currentSku.variationValue,
            variationValue: currentSku.variationValue,
            highlights: (data.facets || []).find(f => f.name === 'ingredientPreferences')?.values || [],
            facets: data.facets || [],
            sentiments: data.sentiments || [],
            strategyId: recommendation.strategy?.id,
            explainabilityTags: data.explainabilityTags || []
        };
    });
}

/**
 * Returns the seed item(s) sent in the recommendations API call for a pod, formatted for
 * Constructor's `data-cnstrc-recommendations-seed-items` tracking attribute.
 * Only pods opted into SEED_ITEM_PODS expose seed items (controlled per-story rollout).
 * @param {string} podId - The Constructor pod id
 * @param {Object} params - The recommendations request params
 * @returns {string|undefined} The seed item id(s) as a comma-separated string, or undefined when not applicable
 */
function getSeedItems(podId, params = {}) {
    if (!SEED_ITEM_PODS.has(podId)) {
        return undefined;
    }

    const { itemIds } = params;

    // Normalize single string or array into a clean comma-separated list: drop non-string/empty/whitespace
    // entries and trim each id, so we never emit trailing commas, surrounding whitespace, or a non-id value.
    const ids = (Array.isArray(itemIds) ? itemIds : [itemIds]).filter(id => typeof id === 'string' && id.trim()).map(id => id.trim());

    return ids.length ? ids.join(',') : undefined;
}

export default {
    transformRecommendationsForCarousel,
    getSeedItems
};
